import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import Spinner from 'components/UI/V3/Spinner';
import styles from './Selector.module.scss';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';

export interface SelectorOption<T> {
    label: string;
    subLabel?: string;
    value: string;
    data?: T;
    icon?: IconProp;
}

export interface SelectorProps<T> {
    label?: string;
    icon?: IconProp;
    error?: string;
    placeholder?: string;
    className?: string;
    options: Array<SelectorOption<T>>;
    value?: string;
    disabled?: boolean;
    required?: boolean;
    loading?: boolean;
    renderOption?: (option: SelectorOption<T>) => React.ReactNode;
    onChange?: (value: string, option?: SelectorOption<T>) => void;
    onSearch?: (query: string) => void;
}

export const Selector = <T,>({
    options,
    className,
    renderOption,
    value,
    onChange,
    onSearch,
    placeholder,
    label,
    error,
    icon,
    disabled,
    required,
    loading
}: SelectorProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const selectRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option.value === value);

    useEffect(() => {
        if (selectedOption) setInputValue(selectedOption.label);
    }, [selectedOption]);

    const handleOptionClick = (option: SelectorOption<T>) => {
        onChange?.(option.value, option);
        setIsOpen(false);
    };

    const handleClearSelection = () => {
        onChange?.('', undefined);
        setInputValue('');
        setIsOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setInputValue(query);
        setIsOpen(true);
        onSearch?.(query);
    };

    const showDropdown = isOpen && inputValue.trim().length > 0;

    return (
        <div className={clsx(styles.root, className)}>
            {label && (
                <Label
                    error={error}
                    required={required}
                    className={styles.label}
                >
                    {label}
                </Label>
            )}

            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <div
                    className={styles.selectWrapper}
                    ref={selectRef}
                >
                    <div
                        className={clsx(styles.select, {
                            [styles.selectOpen]: showDropdown,
                            [styles.selectError]: error,
                            [styles.selectDisabled]: disabled
                        })}
                    >
                        <FontAwesomeIcon
                            icon={faMagnifyingGlass}
                            className={styles.searchIcon}
                            onClick={() => !disabled && setIsOpen(!isOpen)}
                        />
                        {selectedOption ? (
                            <div className={styles.selectedChip}>
                                {selectedOption.icon && (
                                    <FontAwesomeIcon
                                        icon={selectedOption.icon}
                                        className={styles.optionIcon}
                                    />
                                )}
                                <span className={styles.primaryLabel}>{selectedOption.label}</span>
                                {selectedOption.subLabel && (
                                    <span className={styles.secondaryLabel}>{selectedOption.subLabel}</span>
                                )}
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    className={styles.clearIcon}
                                    onClick={handleClearSelection}
                                />
                            </div>
                        ) : (
                            <input
                                type="text"
                                className={styles.inputField}
                                value={inputValue}
                                placeholder={placeholder}
                                disabled={disabled}
                                onChange={handleInputChange}
                                onFocus={() => setIsOpen(true)}
                                onClick={e => e.stopPropagation()}
                            />
                        )}

                        <FontAwesomeIcon
                            icon={icon || faChevronDown}
                            className={clsx(styles.icon, { [styles.iconOpen]: isOpen })}
                            onClick={() => !disabled && setIsOpen(!isOpen)}
                        />
                    </div>

                    {showDropdown && (
                        <div className={styles.dropdown}>
                            {loading ? (
                                <div className={styles.optionLoading}>
                                    <Spinner size={32} />
                                </div>
                            ) : options.length > 0 ? (
                                options.map(option => (
                                    <div
                                        key={option.value}
                                        className={clsx(styles.option, {
                                            [styles.optionSelected]: option.value === value
                                        })}
                                        onClick={() => handleOptionClick(option)}
                                    >
                                        {renderOption ? (
                                            renderOption(option)
                                        ) : (
                                            <div className={styles.optionContent}>
                                                {option.icon && (
                                                    <FontAwesomeIcon
                                                        icon={option.icon}
                                                        className={styles.optionIcon}
                                                    />
                                                )}
                                                <div className={styles.optionLabels}>
                                                    <span className={styles.primaryLabel}>{option.label}</span>
                                                    {option.subLabel && (
                                                        <span className={styles.secondaryLabel}>{option.subLabel}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.optionEmpty}>No results found</div>
                            )}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default Selector;
